import { BorderBox } from "@/components/atoms/BorderBox";
import { isSurveyDescriptionChangedChange, isSurveyNameChangedChange } from "@/features/survey_sync/lib/typeGuards/survey";
import { SurveyLevelDangerousChange } from "@/features/survey_sync/types/syncCheckResult";
import { OldNewValueChange } from "../../atoms/OldNewValueChange";
import { useSelectedItemsForSyncContext } from "@/features/survey_sync/context/selectedItemsForSyncContext";
import { useSurveyCheckResultContext } from "@/features/survey_sync/context/surveyCheckResultContext";
import { Checkbox } from "@/components/ui/checkbox";

interface SurveyLevelChangeProps {
    change: SurveyLevelDangerousChange
}

export function SurveyLevelChange(props: SurveyLevelChangeProps) {
    const surveyCheckResult = useSurveyCheckResultContext();
    const { selectedItemsForSync, setSelectedItemsForSync } = useSelectedItemsForSyncContext();

    const isNameSelectedForSync = selectedItemsForSync?.[surveyCheckResult?.surveyInstance?.id]?.survey?.name;
    const isDescriptionSelectedForSync = selectedItemsForSync?.[surveyCheckResult?.surveyInstance?.id]?.survey?.description;

    const handleNameForSyncChange = () => {
        if (isNameSelectedForSync) {
            setSelectedItemsForSync({
                ...selectedItemsForSync,
                [surveyCheckResult?.surveyInstance.id]: {
                    ...selectedItemsForSync?.[surveyCheckResult?.surveyInstance.id],
                    survey: {
                        ...selectedItemsForSync?.[surveyCheckResult?.surveyInstance.id]?.survey,
                        name: false,
                    }
                }
            });
        }
        else {
            setSelectedItemsForSync({
                ...selectedItemsForSync,
                [surveyCheckResult?.surveyInstance.id]: {
                    ...(selectedItemsForSync?.[surveyCheckResult?.surveyInstance.id] || { survey: {}, questionsToSync: [] }),
                    survey: {
                        ...selectedItemsForSync?.[surveyCheckResult?.surveyInstance.id]?.survey,
                        name: true,
                    }
                }
            });
        }
    }

    const handleDescriptionForSyncChange = () => {
        if (isDescriptionSelectedForSync) {
            setSelectedItemsForSync({
                ...selectedItemsForSync,
                [surveyCheckResult?.surveyInstance.id]: {
                    ...selectedItemsForSync?.[surveyCheckResult?.surveyInstance.id],
                    survey: {
                        ...selectedItemsForSync?.[surveyCheckResult?.surveyInstance.id]?.survey,
                        description: false,
                    }
                }
            });
        }
        else {
            setSelectedItemsForSync({
                ...selectedItemsForSync,
                [surveyCheckResult?.surveyInstance.id]: {
                    ...(selectedItemsForSync?.[surveyCheckResult?.surveyInstance.id] || { survey: {}, questionsToSync: [] }),
                    survey: {
                        ...selectedItemsForSync?.[surveyCheckResult?.surveyInstance.id]?.survey,
                        description: true,
                    }
                }
            });
        }
    }

    if (!props.change) return null;
    if (isSurveyNameChangedChange(props.change)) {
        return (
            <OldNewValueChange
                label={
                    <div className="flex items-center justify-between w-full">
                        <span>Survey name changed</span>
                        <div className="flex items-center gap-2 font-normal text-[#020817] text-sm">
                            <span>Sync survey name</span>
                            <Checkbox
                                checked={isNameSelectedForSync}
                                onCheckedChange={handleNameForSyncChange}
                            />
                        </div>
                    </div>
                }
                oldLabel="From instance"
                oldValue={props.change.context.instanceName}
                newLabel="To template"
                newValue={props.change.context.templateName}
                valueName="name"
            />
        )
    }
    if (isSurveyDescriptionChangedChange(props.change)) {
        return (
            <OldNewValueChange
                label={
                    <div className="flex items-center justify-between w-full">
                        <span>Survey description changed</span>
                        <div className="flex items-center gap-2 font-normal text-[#020817] text-sm">
                            <span>Sync survey description</span>
                            <Checkbox
                                checked={isDescriptionSelectedForSync}
                                onCheckedChange={handleDescriptionForSyncChange}
                            />
                        </div>
                    </div>
                }
                oldValue={props.change.context.instanceDescription}
                oldLabel="From instance"
                newValue={props.change.context.templateDescription}
                newLabel="To template"
                valueName="description"
            />
        )
    }
    return (
        <BorderBox size={"sm"} className="flex justify-between">
            <h4 className="font-semibold">Unsupported survey change</h4>
            <div className="text-sm text-muted-foreground">{props.change.type}</div>
        </BorderBox>
    )
}